import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Chip, CircularProgress, TextField } from "@mui/material";
import { lookupVendorMakes } from "services/vendorMakeService";

const VendorMakeSelections = ({ selections = [], onChange, onRemove, children }) => {
  const [lookup, setLookup] = useState({ bySubCategory: {}, selectedMakes: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [searchTextBySubCategory, setSearchTextBySubCategory] = useState({});
  const previous = useRef(new Set());
  const callbacks = useRef({ onRemove });
  callbacks.current = { onRemove };
  const subKey = JSON.stringify([...new Set(selections.map((entry) => entry.subCategoryId))].sort());
  const makeKey = JSON.stringify([...new Set(selections.flatMap((entry) => entry.makeIds))].sort());

  useEffect(() => {
    const current = new Set(JSON.parse(subKey));
    const removed = [...previous.current].filter((id) => !current.has(id));
    previous.current = current;
    if (removed.length) callbacks.current.onRemove?.(removed);
  }, [subKey]);

  useEffect(() => {
    const subCategoryIds = JSON.parse(subKey);
    if (!subCategoryIds.length) { setLoading(false); setError(""); return undefined; }
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const timer = setTimeout(() => {
      lookupVendorMakes(subCategoryIds, JSON.parse(makeKey), controller.signal)
        .then((data) => { if (!controller.signal.aborted) setLookup(data); })
        .catch(() => { if (!controller.signal.aborted) setError("Unable to load makes. Existing selections are preserved."); })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 200);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [subKey, makeKey, attempt]);

  const savedNames = new Map(lookup.selectedMakes.map((make) => [String(make._id), make]));
  const entries = new Map(selections.map((entry) => [String(entry.subCategoryId), entry]));
  const selectionData = (entry) => {
    const available = lookup.bySubCategory[entry.subCategoryId] || [];
    const options = new Map(available.map((make) => [String(make._id), { ...make, available: true }]));
    const value = entry.makeIds.map((id) => options.get(id) || { ...(savedNames.get(id) || { _id: id, name: "Previously selected make (" + id.slice(-6) + ")" }), available: false });
    value.forEach((make) => { if (!options.has(String(make._id))) options.set(String(make._id), make); });
    return { available, options: [...options.values()], value };
  };

  const renderSelection = (subCategoryId, { summary = false } = {}) => {
    const entry = entries.get(String(subCategoryId));
    if (!entry) return null;
    const entryId = String(entry.subCategoryId);
    const { available, options, value } = selectionData(entry);
    if (summary) return (
        <>
          {value.map((make) => (
            <span key={String(make._id)} title={make.name + (make.available ? "" : " (unavailable)")} className={"max-w-full break-words rounded-full border px-2 py-0.5 text-[11px] " + (make.available ? "border-[#F8DBC5] bg-[#FFF3E8] text-[#924513]" : "border-gray-200 bg-gray-50 text-gray-500")}>
              {make.name}{make.available ? "" : " (unavailable)"}
            </span>
          ))}
          {!value.length && <span className="text-[11px] text-gray-400">Makes optional</span>}
        </>
    );
    return (
      <div role="group" aria-label={"Makes for " + entry.label} className="min-w-0 w-full">
        <Autocomplete multiple size="small" fullWidth disableCloseOnSelect options={options} value={value}
          inputValue={searchTextBySubCategory[entryId] || ""}
          loading={loading} disabled={loading && !available.length && !value.length}
          componentsProps={{ popper: { sx: { zIndex: 10050 } } }}
          onKeyDown={(event) => { if (event.key === "Enter") event.preventDefault(); }}
          isOptionEqualToValue={(option, selected) => String(option._id) === String(selected._id)}
          getOptionLabel={(option) => option.name} getOptionDisabled={(option) => !option.available}
          onInputChange={(_, nextSearchText, reason) => {
            if (reason !== "input" && reason !== "clear") return;
            setSearchTextBySubCategory((current) => (
              current[entryId] === nextSearchText
                ? current
                : { ...current, [entryId]: nextSearchText }
            ));
          }}
          onChange={(_, selected) => {
            setSearchTextBySubCategory((current) => (
              current[entryId]
                ? { ...current, [entryId]: "" }
                : current
            ));
            onChange(entry.subCategoryId, selected.map((make) => String(make._id)));
          }}
          noOptionsText={error ? "Make options unavailable" : "No makes listed for this subcategory"}
          renderTags={(selected, getTagProps) => selected.map((make, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return <Chip key={key} {...tagProps} size="small" label={make.name + (make.available ? "" : " (unavailable)")} variant={make.available ? "filled" : "outlined"} />;
          })}
          renderInput={(params) => <TextField {...params} label="Makes / Brands (optional)" inputProps={{ ...params.inputProps, "aria-label": "Makes / Brands for " + entry.label + " (optional)" }} placeholder={value.length ? "Add make" : "Select makes"}
            helperText={!loading && !error && !available.length ? "No makes listed. You can continue without selecting one." : undefined}
            InputProps={{ ...params.InputProps, endAdornment: <>{loading && <CircularProgress size={14} sx={{ color: "#FD6600" }} />}{params.InputProps.endAdornment}</> }} />}
          sx={{ backgroundColor: "white", "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "12px" }, "& .MuiInputLabel-root": { fontSize: "12px" }, "& .MuiChip-root": { maxWidth: "100%", fontSize: "11px" }, "& .MuiChip-filled": { backgroundColor: "#FFF0E5", color: "#A84600" } }} />
        {error && <div className="mt-1 flex items-start gap-2">
          <p role="alert" className="text-[11px] text-amber-800">{error}</p>
          <button type="button" disabled={loading} onClick={() => setAttempt((current) => current + 1)} className="shrink-0 text-[11px] font-semibold text-[#A84600] underline disabled:opacity-50">Retry</button>
        </div>}
      </div>
    );
  };

  return children(renderSelection);
};

export default VendorMakeSelections;
